import PropTypes from 'prop-types'
import { useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { object, string } from 'yup'

import Button from '@/components/Button'
import Form, { Text } from '@/components/Form'
import { ImageLibraryWithFormik } from '@/components/ImageLibrary'

import messages from './messages'

const imageLibrarySchema = object().shape({
  displayTitle: string()
    .trim()
    .max(64, 'Text is too long.')
    // @ts-expect-error
    .required(<FormattedMessage {...messages.requiredError} />)
})

const AddNotificationSource = ({
  notificationSource,
  isEditing,
  isActionInProgress,
  onCreate,
  onUpdate,
  onCancel
}: any) => {
  const intl = useIntl()

  const onCancelImageCallback = useRef(() => {})

  const cancel = (values: any, { resetForm }: any) => {
    // First ask the image input to do what it needs because we are canceling!
    onCancelImageCallback.current()
    onCancel()
    resetForm({ displayTitle: '', iconUrl: '' })
  }

  const onSubmit = (values: any, { resetForm }: any) => {
    if (isEditing) {
      onUpdate(values)
    } else {
      onCreate(values)
    }
    resetForm({ displayTitle: '', iconUrl: '' })
  }

  const registerOnCancel = (cancelCallback: any) => {
    onCancelImageCallback.current = cancelCallback
  }

  return (
    <Form
      buttons={
        <>
          <Button color="secondary" type="reset">
            {intl.formatMessage(messages.cancelButton)}
          </Button>
          <Button color="primary" type="submit">
            {intl.formatMessage(isEditing ? messages.updateButton : messages.createButton)}
          </Button>
        </>
      }
      initialValues={notificationSource}
      isActionInProgress={isActionInProgress}
      onReset={cancel}
      onSubmit={onSubmit}
      enableReinitialize={true}
      schema={imageLibrarySchema}
      title={
        isEditing ? <FormattedMessage {...messages.updateTitle} /> : <FormattedMessage {...messages.createTitle} />
      }
    >
      <p>
        <FormattedMessage {...messages.description} />
      </p>
      <Text
        label={<FormattedMessage {...messages.displayTitle} />}
        name="displayTitle"
        placeholder={intl.formatMessage(messages.displayTitlePlaceholder)}
        required
        spellCheck={true}
      />

      {/* @ts-expect-error */}
      <ImageLibraryWithFormik
        required
        label={intl.formatMessage(messages.iconUrl)}
        name="iconUrl"
        placeholder={intl.formatMessage(messages.iconUrlPlaceholder)}
        cancel={registerOnCancel}
      />
    </Form>
  )
}

AddNotificationSource.displayName = 'AddNotificationSource'

AddNotificationSource.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  isActionInProgress: PropTypes.bool.isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  notificationSource: PropTypes.shape({
    id: PropTypes.string,
    displayTitle: PropTypes.string,
    iconUrl: PropTypes.string
  }).isRequired
}

export default AddNotificationSource
